import { z } from 'zod';

import { ColumnSettingsSchema } from '../../shared/ColumnSelectPro/types';
import type { ColumnSettingsType } from '../../shared/ColumnSelectPro/types';
import { ImportRequestSchema, ImportResponseSchema } from '../../shared/ColumnSelectPro/currentImport';
import type { ImportRequestType, ImportResponseType } from '../../shared/ColumnSelectPro/currentImport';
import { Api } from '@automapa/svelte-automapa-template';

export const ImportAnalyzeContextReasonSchema = z.object({
    reason: z.string(),
    value: z.string().or(z.array(z.string())).catch(''),
    original_values: z.array(z.string()).catch([]),
    index: z.number().nullable().catch(null),
    checks: z.array(
        z.object({
            desc: z.string(),
            valid: z.boolean(),
        })
    ),
});
export type ImportAnalyzeContextReasonType = z.infer<typeof ImportAnalyzeContextReasonSchema>;

export const ImportAnalyzeContextItemSchema = z.object({
    count: z.coerce.number(),
    values: z.array(z.any()).catch([]),
    reasons: z.array(ImportAnalyzeContextReasonSchema).catch([]),
    by_category: z.any(), // z.record(z.string(), z.number()).catch({ invalid: 1 }),
});
export type ImportAnalyzeContextItemType = z.infer<typeof ImportAnalyzeContextItemSchema>;

export const ImportAnalyzeResponseSchema = z.object({
    /* needed for analyze info when upsert */
    added: ImportAnalyzeContextItemSchema,
    changed: ImportAnalyzeContextItemSchema,
    unchanged: ImportAnalyzeContextItemSchema,
    removed: ImportAnalyzeContextItemSchema,

    /* needed for analyze info when importing with coords or address */
    added_by_address: ImportAnalyzeContextItemSchema,
    added_by_coords: ImportAnalyzeContextItemSchema,
    added_by_xy: ImportAnalyzeContextItemSchema,
    needed_to_resolve_from_link: ImportAnalyzeContextItemSchema,

    total_uploaded: ImportAnalyzeContextItemSchema,
    uploaded_ignored: ImportAnalyzeContextItemSchema,
    invalid: ImportAnalyzeContextItemSchema,
    extra: z.any(),
    errors: z.array(z.string()).catch([]),
    count_items_after_import: z.number(),
    max_items: z.number(),
});
export type ImportAnalyzeResponseType = z.infer<typeof ImportAnalyzeResponseSchema>;

export class ColumnSettingsApi {
    public static async get(api: Api, id: number): Promise<ImportResponseType> {
        return api.getData<undefined, ImportResponseType>('get', `/import/${id}`, undefined, z.undefined(), ImportResponseSchema);
    }

    // https://lm.nemo.indigo/docs/#req_589c3015f909451fb4ba809b123b149b
    public static async update(api: Api, id: number, data: ImportRequestType): Promise<ImportResponseType> {
        return api.getData<ImportRequestType, ImportResponseType>('put', `/import/${id}`, data, ImportRequestSchema, ImportResponseSchema);
    }

    public static async analyze(api: Api, id: number): Promise<ImportAnalyzeResponseType> {
        return api.getData<any, ImportAnalyzeResponseType>('post', `/import/${id}/analyze`, {}, z.object({}), ImportAnalyzeResponseSchema);
    }

    public static async confirm(api: Api, id: number): Promise<any> {
        return api.getData<any, any>('post', `/import/${id}/confirm`, {}, z.object({}), z.object({ message: z.literal('OK') }));
    }

    // https://lm.nemo.indigo/docs/#req_a46344812b6745178984d8ed11b5eb84
    public static async updateColumnSettings(api: Api, id: number, data: ColumnSettingsType): Promise<ImportResponseType> {
        return api.getData<ColumnSettingsType, ImportResponseType>(
            'put',
            `/import/${id}/columns_assignment`,
            data,
            ColumnSettingsSchema,
            ImportResponseSchema
        );
    }
}
